import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    getCountFromServer,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    increment,
    arrayUnion
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";
import { fs, functions } from "../firebase.ts";
import { getDeviceID } from "../components/global.ts";

// B-Link : email inbox is my storage for now
// TODO: later: save links to firestore

export interface Link {
    id?: string; // doc id => b.333.eco/l/id
    date: Timestamp;
    author: string; // uid

    title: string;
    description?: string;
    thumbnail?: string;

    // stats: from here
    digs?: number; // total digs
    diggers?: string[]; // device id of diggers
    miners?: string[]; // uid of miners

    // stats: b app
    previews?: number;
    crawlers?: Array<string>;
    scans?: number; // total scans
    scanners?: string[]; // device id of scanners
}

type Response = {
    text: string;
    color: "green" | "red" | "orange"; // status
    data?: Link;
};

export class Links {
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.collection = collection(fs, "links");
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    async get(id: string): Promise<Link | null> {
        const docRef = doc(fs, "links", id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists()
            ? ({ id: docSnap.id, ...docSnap.data() } as Link)
            : null;
    }

    // temp
    async broadcast(url: string, cite: string): Promise<Response> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "browser/link" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "browser/link",
                    url,
                    cite
                })
            ).data as Link;

            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async publish(
        title: string,
        description: string,
        thumbnail: string
    ): Promise<Response> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "browser/link" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "browser/link",
                    title,
                    description,
                    thumbnail
                })
            ).data as Link;

            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async addMiner(uid: string, id: string) {
        const docRef = doc(fs, "links", id);
        uid
            ? await updateDoc(docRef, {
                  digs: increment(1),
                  miners: arrayUnion(uid)
              })
            : await updateDoc(docRef, {
                  digs: increment(1),
                  diggers: arrayUnion(getDeviceID())
              });
    }

    // not allowed bc blockchain
    async remove(id: string) {
        const docRef = doc(fs, "links", id);
        await deleteDoc(docRef);
    }
}
